import { type JSX, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Icon } from '@/components/icons';
import { Button, Input, Spinner } from '@/components/ui';
import { api, ApiError } from '@/lib/api';
import { setTokens } from '@/lib/auth';

/**
 * Accept-invite landing page. Pairs with the magic-link in the
 * KindTeamInvitation email that AdminCustomersService.Create sends.
 *
 * Query string carries the raw token (?token=XXX). The form POSTs to
 * /v1/auth/accept-invite with {token, password, full_name?}.
 *
 * On success the API returns AuthTokens; we stash them via setTokens
 * + redirect straight to /dashboard so the new owner doesn't have to
 * re-enter the password they just chose.
 */

const schema = z
  .object({
    fullName: z.string().min(1).optional().or(z.literal('')),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(8, 'Confirm your password'),
  })
  .refine((v) => v.password === v.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type FormValues = z.infer<typeof schema>;

interface AcceptInviteResponse {
  access_token: string;
  refresh_token: string;
}

export default function AcceptInvitePage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const navigate = useNavigate();
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<FormValues>({
    defaultValues: { fullName: '', password: '', confirmPassword: '' },
  });

  if (!token) {
    return (
      <div className="w-full min-h-screen grid place-items-center bg-paper px-6">
        <div className="w-full max-w-[420px] text-center">
          <h1 className="display text-[24px] font-semibold text-text">Invalid invite link</h1>
          <p className="text-[14px] text-text-3 mt-2 mb-6">
            The link you followed is missing the token. Ask the admin who created your
            workspace to send a fresh invite.
          </p>
          <Link to="/login" className="text-[14px] text-volt-500 font-medium hover:underline">
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  const onSubmit = handleSubmit(async (values) => {
    setErrorMsg(null);
    const parsed = schema.safeParse(values);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const path = issue.path[0];
        if (path === 'fullName' || path === 'password' || path === 'confirmPassword') {
          setError(path, { message: issue.message });
        }
      }
      return;
    }
    try {
      const res = await api.post<AcceptInviteResponse>('/v1/auth/accept-invite', {
        token,
        password: parsed.data.password,
        full_name: parsed.data.fullName || undefined,
      });
      setTokens(res.access_token, res.refresh_token);
      // Bounce straight into the app — the user just authenticated by
      // choosing a password, no need to make them type it again on
      // /login.
      navigate('/dashboard', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'invalid_invite' || err.status === 401) {
          setErrorMsg(
            'This invite link is invalid, expired, or has already been used. Ask the admin to resend.',
          );
        } else if (err.code === 'validation_error') {
          setErrorMsg(err.message);
        } else if (err.code === 'tenant_suspended') {
          setErrorMsg(err.message);
        } else {
          setErrorMsg('Something went wrong. Please try again in a moment.');
        }
      } else {
        setErrorMsg('Network error. Check your connection and retry.');
      }
    }
  });

  return (
    <div className="w-full min-h-screen grid place-items-center bg-paper px-6">
      <div className="w-full max-w-[420px]">
        <h1 className="display text-[28px] font-semibold tracking-[-0.02em] text-text leading-[1.15] m-0">
          Welcome to EzSender
        </h1>
        <p className="text-[14.5px] text-text-3 mt-2.5 mb-7 leading-relaxed">
          Set a password to activate your account. After this, you can sign in any time at{' '}
          <span className="text-text-2 font-medium">app.getezsender.com</span>.
        </p>

        <form onSubmit={onSubmit} noValidate>
          {errorMsg && (
            <div
              role="alert"
              className="flex gap-2.5 items-start px-3.5 py-3 bg-danger-bg border border-danger-bd rounded-md mb-5"
            >
              <Icon.warn width="18" height="18" className="text-danger flex-shrink-0 mt-px" />
              <div className="text-[13px] text-danger leading-relaxed">{errorMsg}</div>
            </div>
          )}

          <div className="mb-4">
            <label
              htmlFor="invite-full-name"
              className="block text-[12px] font-medium text-text-2 mb-1.5"
            >
              Your name <span className="text-text-4 font-normal">(optional)</span>
            </label>
            <Input
              id="invite-full-name"
              type="text"
              autoComplete="name"
              placeholder="As you want it to appear"
              disabled={isSubmitting}
              error={Boolean(errors.fullName)}
              {...register('fullName')}
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="invite-password"
              className="block text-[12px] font-medium text-text-2 mb-1.5"
            >
              Password
            </label>
            <Input
              id="invite-password"
              type="password"
              autoComplete="new-password"
              disabled={isSubmitting}
              error={Boolean(errors.password)}
              {...register('password')}
            />
            {errors.password && (
              <p className="text-[12px] text-danger mt-1.5">{errors.password.message}</p>
            )}
          </div>

          <div className="mb-5">
            <label
              htmlFor="invite-confirm-password"
              className="block text-[12px] font-medium text-text-2 mb-1.5"
            >
              Confirm password
            </label>
            <Input
              id="invite-confirm-password"
              type="password"
              autoComplete="new-password"
              disabled={isSubmitting}
              error={Boolean(errors.confirmPassword)}
              {...register('confirmPassword')}
            />
            {errors.confirmPassword && (
              <p className="text-[12px] text-danger mt-1.5">{errors.confirmPassword.message}</p>
            )}
          </div>

          <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Spinner />
                <span>Activating…</span>
              </>
            ) : (
              <span>Activate account &amp; sign in</span>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
